import { Observable } from 'rxjs';

import { IFsMap } from '@lib/geo/core';
import { SnLonLat } from '@lib/geo/helpers';
import { SnBadgeConfig } from '@lib/ui/badge';

import { MapMarkerIcon } from '../../core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '../../core/marker/marker-options';
import { FsMapPopup } from '../../core/popup';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from './marker-events.interface';
import { IMarkerOptions } from './marker-options.interface';
import { IMarker } from './marker.interface';

export interface IMarkerFacade<TMap extends IFsMap> {
  // Core marker operations
  addMarker(marker: IMarkerOptions): IMarker<TMap>;
  removeMarker(marker: IMarker<TMap>): void;
  updateMarker(marker: IMarker<TMap>, options: Partial<IMarkerOptions>): void;

  // Marker lifecycle and state management
  setVisible(marker: IMarker<TMap>, visible: boolean): void;
  setPosition(marker: IMarker<TMap>, position: SnLonLat): void;
  setIcon(marker: IMarker<TMap>, icon: MapMarkerIcon): void;
  setStyle(marker: IMarker<TMap>, style: IMapMarkerStyleOptions): void;
  setDraggable(marker: IMarker<TMap>, draggable: boolean): void;
  setRotation(marker: IMarker<TMap>, rotation: number): void;
  setTitle(marker: IMarker<TMap>, title: string): void;

  // Marker properties and data access
  getMarkerId(marker: IMarker<TMap>): string;
  getMarkerPosition(marker: IMarker<TMap>): SnLonLat;
  getMarkerTitle(marker: IMarker<TMap>): string;
  getMarkerAttributes(marker: IMarker<TMap>): Record<string, unknown>;
  getMarkerGeometry(marker: IMarker<TMap>): unknown;
  getMarkerCenter(marker: IMarker<TMap>): { center: number[] };

  // Badge management
  setBadge(marker: IMarker<TMap>, badge: SnBadgeConfig): void;
  getBadge(marker: IMarker<TMap>): SnBadgeConfig;
  createBadge(marker: IMarker<TMap>, badgeClass?: string): void;
  createSelectedBadge(marker: IMarker<TMap>): void;
  removeSelectedBadge(marker: IMarker<TMap>): void;
  setBadgeStatus(
    marker: IMarker<TMap>,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void;

  // Popup management
  setPopup(marker: IMarker<TMap>, popup: FsMapPopup<TMap>): void;

  // Event handling - Mouse and Touch Events
  onMarkerClick(marker: IMarker<TMap>): Observable<IMarkerClickEvent<TMap>>;
  onMarkerDoubleClick(marker: IMarker<TMap>): Observable<IMarkerDoubleClickEvent<TMap>>;
  onMarkerRightClick(marker: IMarker<TMap>): Observable<IMarkerRightClickEvent<TMap>>;
  onMarkerMouseOver(marker: IMarker<TMap>): Observable<IMarkerMouseOverEvent<TMap>>;
  onMarkerMouseOut(marker: IMarker<TMap>): Observable<IMarkerMouseOutEvent<TMap>>;
  onMarkerMouseEnter(marker: IMarker<TMap>): Observable<IMarkerMouseEnterEvent<TMap>>;
  onMarkerMouseLeave(marker: IMarker<TMap>): Observable<IMarkerMouseLeaveEvent<TMap>>;

  // Event handling - Drag Events
  onMarkerDragStart(marker: IMarker<TMap>): Observable<IMarkerDragStartEvent<TMap>>;
  onMarkerDrag(marker: IMarker<TMap>): Observable<IMarkerDragMoveEvent<TMap>>;
  onMarkerDragEnd(marker: IMarker<TMap>): Observable<IMarkerDragEndEvent<TMap>>;

  // Event handling - State Change Events
  onMarkerPositionChange(marker: IMarker<TMap>): Observable<IMarkerPositionChangeEvent<TMap>>;
  onMarkerTitleChange(marker: IMarker<TMap>): Observable<IMarkerTitleChangeEvent<TMap>>;
  onMarkerVisibilityChange(marker: IMarker<TMap>): Observable<IMarkerVisibilityChangeEvent<TMap>>;
  onMarkerSelectionChange(marker: IMarker<TMap>): Observable<IMarkerSelectionChangeEvent<TMap>>;
  onMarkerIconChange(marker: IMarker<TMap>): Observable<IMarkerIconChangeEvent<TMap>>;
  onMarkerStyleChange(marker: IMarker<TMap>): Observable<IMarkerStyleChangeEvent<TMap>>;

  // Event handling - Lifecycle Events
  onMarkerAdd(marker: IMarker<TMap>): Observable<IMarkerAddEvent<TMap>>;
  onMarkerRemove(marker: IMarker<TMap>): Observable<IMarkerRemoveEvent<TMap>>;
  onMarkerDestroy(marker: IMarker<TMap>): Observable<IMarkerDestroyEvent<TMap>>;

  // Event handling - All Events (for general event listening)
  onMarkerEvents(marker: IMarker<TMap>): Observable<IMarkerEventUnion<TMap>>;

  // Marker query and search
  findMarkerById(id: string): IMarker<TMap> | null;
  getAllMarkers(): IMarker<TMap>[];
  getMarkersByAttribute(attribute: string, value: unknown): IMarker<TMap>[];
}
